import { LitElement, html, css } from 'lit';

export class QuizMode extends LitElement {
  static styles = css`
    :host {
      display: block;
    }

    .quiz-container {
      background: white;
      border-radius: 20px;
      padding: 24px;
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
      transition: background-color 0.3s ease;
    }

    .quiz-container.correct-answer {
      background: rgba(76, 175, 80, 0.15);
    }

    .quiz-container.incorrect-answer {
      background: rgba(244, 67, 54, 0.15);
    }

    .question-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
    }

    .question-number {
      font-size: 0.875rem;
      color: #666;
      font-weight: 500;
    }

    .score {
      font-size: 0.875rem;
      font-weight: 600;
      color: #667eea;
      background: rgba(102, 126, 234, 0.1);
      padding: 6px 14px;
      border-radius: 20px;
    }

    .question {
      font-size: 1.25rem;
      font-weight: 600;
      color: #333;
      margin-bottom: 24px;
      line-height: 1.5;
    }

    .word-highlight {
      color: #667eea;
      font-weight: 700;
    }

    .options {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .option {
      padding: 16px 20px;
      border: 2px solid #e0e0e0;
      border-radius: 12px;
      cursor: pointer;
      transition: all 0.2s ease;
      font-size: 1rem;
      text-align: left;
      background: white;
    }

    .option:hover:not(.disabled) {
      border-color: #667eea;
      background: rgba(102, 126, 234, 0.05);
    }

    .option.correct {
      border-color: #4caf50;
      background: rgba(76, 175, 80, 0.2);
      font-weight: 600;
    }

    .option.incorrect {
      border-color: #f44336;
      background: rgba(244, 67, 54, 0.1);
      opacity: 0.7;
    }

    .option.disabled {
      cursor: not-allowed;
      pointer-events: none;
      opacity: 0.6;
    }

    .option.disabled.correct {
      opacity: 1;
      cursor: default;
    }

    .feedback-status {
      text-align: center;
      font-weight: 600;
      font-size: 1rem;
      margin: 16px 0;
      min-height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }

    .feedback-status.correct {
      color: #4caf50;
    }

    .feedback-status.incorrect {
      color: #f44336;
    }

    .next-btn {
      width: 100%;
      padding: 16px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      border: none;
      border-radius: 12px;
      font-size: 1rem;
      font-weight: 600;
      cursor: pointer;
      transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
    }

    .next-btn:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
    }

    .next-btn:disabled {
      opacity: 0;
      pointer-events: none;
    }

    .progress-bar {
      height: 6px;
      background: #e0e0e0;
      border-radius: 3px;
      margin-bottom: 20px;
      overflow: hidden;
    }

    .progress-fill {
      height: 100%;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border-radius: 3px;
      transition: width 0.3s ease;
    }

    .results {
      text-align: center;
      padding: 20px 0;
    }

    .results-score {
      font-size: 3rem;
      font-weight: 700;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      margin-bottom: 8px;
    }

    .results-text {
      font-size: 1.125rem;
      color: #666;
      margin-bottom: 24px;
    }

    .restart-btn {
      padding: 16px 32px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      border: none;
      border-radius: 12px;
      font-size: 1rem;
      font-weight: 600;
      cursor: pointer;
      transition: transform 0.2s ease, box-shadow 0.2s ease;
    }

    .restart-btn:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
    }

    @media (max-width: 480px) {
      .quiz-container {
        padding: 20px;
      }

      .question {
        font-size: 1.1rem;
      }

      .option {
        padding: 14px 16px;
        font-size: 0.95rem;
      }
    }
  `;

  static properties = {
    words: { type: Array },
    currentIndex: { type: Number },
    score: { type: Number },
    answered: { type: Boolean },
    selectedOption: { type: Number },
    showResults: { type: Boolean },
    shuffledOptions: { type: Array }
  };

  constructor() {
    super();
    this.words = [];
    this.currentIndex = 0;
    this.score = 0;
    this.answered = false;
    this.selectedOption = null;
    this.showResults = false;
    this.shuffledOptions = [];
    this._autoAdvanceTimeout = null;
  }

  updated(changedProperties) {
    if (changedProperties.has('words') || changedProperties.has('currentIndex')) {
      this._generateOptions();
    }
  }

  _generateOptions() {
    if (!this.words.length || this.currentIndex >= this.words.length) return;
    
    const currentWord = this.words[this.currentIndex];
    const otherWords = this.words.filter((_, i) => i !== this.currentIndex);
    const randomOthers = otherWords.sort(() => Math.random() - 0.5).slice(0, 3);
    
    this.shuffledOptions = [
      { text: currentWord.meaning, correct: true },
      ...randomOthers.map(w => ({ text: w.meaning, correct: false }))
    ].sort(() => Math.random() - 0.5);
  }

  _selectOption(index) {
    if (this.answered) return;
    
    this.answered = true;
    this.selectedOption = index;
    
    const isCorrect = this.shuffledOptions[index].correct;
    if (isCorrect) {
      this.score++;
    }

    this.dispatchEvent(new CustomEvent('answer', {
      detail: { 
        correct: isCorrect,
        word: this.words[this.currentIndex]
      },
      bubbles: true,
      composed: true
    }));

    // Auto-advance after 500ms if answer is correct
    if (isCorrect) {
      this._autoAdvanceTimeout = setTimeout(() => {
        this._nextQuestion();
      }, 500);
    }
  }

  _nextQuestion() {
    // Clear any pending auto-advance timeout
    if (this._autoAdvanceTimeout) {
      clearTimeout(this._autoAdvanceTimeout);
      this._autoAdvanceTimeout = null;
    }

    // Clear the answered state
    this.answered = false;
    this.selectedOption = null;

    if (this.currentIndex < this.words.length - 1) {
      this.currentIndex++;
      // Generate new options immediately for the next question
      this._generateOptions();
    } else {
      this.showResults = true;
    }
  }

  _restartQuiz() {
    this.currentIndex = 0;
    this.score = 0;
    this.answered = false;
    this.selectedOption = null;
    this.showResults = false;
    this._generateOptions();
    
    this.dispatchEvent(new CustomEvent('restart', {
      bubbles: true,
      composed: true
    }));
  }

  _getAnswerStatus() {
    if (!this.answered) return '';
    return this.shuffledOptions[this.selectedOption].correct ? 'correct' : 'incorrect';
  }

  render() {
    if (this.showResults) {
      const percentage = Math.round((this.score / this.words.length) * 100);
      let message = '';
      if (percentage >= 80) message = 'Geweldig gedaan!';
      else if (percentage >= 60) message = 'Goed gedaan!';
      else if (percentage >= 40) message = 'Blijf oefenen!';
      else message = 'Geef niet op!';

      return html`
        <div class="quiz-container">
          <div class="results">
            <div class="results-score">${this.score}/${this.words.length}</div>
            <div class="results-text">${message}</div>
            <button class="restart-btn" @click="${this._restartQuiz}">
              Opnieuw Proberen
            </button>
          </div>
        </div>
      `;
    }

    if (!this.words.length) return html``;

    const currentWord = this.words[this.currentIndex];
    const progress = ((this.currentIndex + 1) / this.words.length) * 100;
    const answerStatus = this._getAnswerStatus();

    return html`
      <div class="quiz-container ${answerStatus ? answerStatus + '-answer' : ''}">
        <div class="progress-bar">
          <div class="progress-fill" style="width: ${progress}%"></div>
        </div>
        
        <div class="question-header">
          <span class="question-number">Vraag ${this.currentIndex + 1} van ${this.words.length}</span>
          <span class="score">Score: ${this.score}</span>
        </div>

        <div class="question">
          Wat is de betekenis van <span class="word-highlight">"${currentWord.word}"</span>?
        </div>

        <div class="options">
          ${this.shuffledOptions.map((option, index) => html`
            <button
              class="option ${this.answered ? (option.correct ? 'correct' : index === this.selectedOption ? 'incorrect' : '') : ''} ${this.answered ? 'disabled' : ''}"
              @click="${() => this._selectOption(index)}"
              ?disabled="${this.answered}"
            >
              ${option.text}
            </button>
          `)}
        </div>

        <div class="feedback-status ${answerStatus}">
          ${answerStatus === 'correct' ? '✓ Goed!' : answerStatus === 'incorrect' ? '✗ Fout' : ''}
        </div>
        
        <button 
          class="next-btn" 
          @click="${this._nextQuestion}"
          ?disabled="${!this.answered}"
        >
          ${this.currentIndex < this.words.length - 1 ? 'Volgende Vraag' : 'Bekijk Resultaat'}
        </button>
      </div>
    `;
  }
}

customElements.define('quiz-mode', QuizMode);
